import {
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  MenuItem,
  InputLabel,
  Box,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import type { FormField } from '../../../types/form-schema.types';

interface FieldRendererProps {
  field: FormField;
  lang?: string;
}

export function FieldRenderer({ field, lang = 'de' }: FieldRendererProps) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const label = field.labels[lang] || field.labels['de'] || field.id;
  const placeholder = field.placeholders[lang] || field.placeholders['de'];
  const hint = field.hints[lang] || field.hints['de'];
  const error = errors[field.processVariable];

  if (field.conditional) {
    const watchedValue = watch(field.conditional.field);
    const shouldShow = evaluateCondition(watchedValue, field.conditional);
    if (!shouldShow) {
      return null;
    }
  }

  switch (field.type) {
    case 'TEXT':
    case 'EMAIL':
    case 'PHONE':
    case 'IBAN':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <TextField
              fullWidth
              label={label}
              placeholder={placeholder}
              helperText={error?.message?.toString() || hint}
              error={!!error}
              value={value || ''}
              onChange={onChange}
              inputRef={ref}
              required={field.required}
              disabled={field.readOnly}
              type={field.type === 'EMAIL' ? 'email' : 'text'}
              margin="normal"
            />
          )}
        />
      );

    case 'NUMBER':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <TextField
              fullWidth
              type="number"
              label={label}
              placeholder={placeholder}
              helperText={error?.message?.toString() || hint}
              error={!!error}
              value={value || ''}
              onChange={onChange}
              inputRef={ref}
              required={field.required}
              disabled={field.readOnly}
              margin="normal"
            />
          )}
        />
      );

    case 'TEXTAREA':
    case 'RICH_TEXT':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <TextField
              fullWidth
              multiline
              rows={4}
              label={label}
              placeholder={placeholder}
              helperText={error?.message?.toString() || hint}
              error={!!error}
              value={value || ''}
              onChange={onChange}
              inputRef={ref}
              required={field.required}
              disabled={field.readOnly}
              margin="normal"
            />
          )}
        />
      );

    case 'DATE':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <TextField
              fullWidth
              type="date"
              label={label}
              helperText={error?.message?.toString() || hint}
              error={!!error}
              value={value || ''}
              onChange={onChange}
              inputRef={ref}
              required={field.required}
              disabled={field.readOnly}
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
          )}
        />
      );

    case 'DATETIME':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <TextField
              fullWidth
              type="datetime-local"
              label={label}
              helperText={error?.message?.toString() || hint}
              error={!!error}
              value={value || ''}
              onChange={onChange}
              inputRef={ref}
              required={field.required}
              disabled={field.readOnly}
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
          )}
        />
      );

    case 'CHECKBOX':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue === 'true'}
          render={({ field: { onChange, value, ref } }) => (
            <FormControl error={!!error} margin="normal">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    inputRef={ref}
                    disabled={field.readOnly}
                  />
                }
                label={label}
              />
              {(error || hint) && (
                <FormHelperText>{error?.message?.toString() || hint}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      );

    case 'RADIO':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <FormControl
              component="fieldset"
              error={!!error}
              margin="normal"
              required={field.required}
            >
              <FormLabel component="legend">{label}</FormLabel>
              <RadioGroup
                value={value || ''}
                onChange={onChange}
                ref={ref}
              >
                {field.options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio disabled={field.readOnly} />}
                    label={option.labels[lang] || option.labels['de'] || option.value}
                  />
                ))}
              </RadioGroup>
              {(error || hint) && (
                <FormHelperText>{error?.message?.toString() || hint}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      );

    case 'SELECT':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <FormControl
              fullWidth
              error={!!error}
              margin="normal"
              required={field.required}
            >
              <InputLabel>{label}</InputLabel>
              <Select
                value={value || ''}
                onChange={onChange}
                label={label}
                inputRef={ref}
                disabled={field.readOnly}
              >
                {field.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.labels[lang] || option.labels['de'] || option.value}
                  </MenuItem>
                ))}
              </Select>
              {(error || hint) && (
                <FormHelperText>{error?.message?.toString() || hint}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      );

    case 'MULTISELECT':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue?.split(',') || []}
          render={({ field: { onChange, value, ref } }) => (
            <FormControl
              fullWidth
              error={!!error}
              margin="normal"
              required={field.required}
            >
              <InputLabel>{label}</InputLabel>
              <Select
                multiple
                value={value || []}
                onChange={onChange}
                label={label}
                inputRef={ref}
                disabled={field.readOnly}
              >
                {field.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.labels[lang] || option.labels['de'] || option.value}
                  </MenuItem>
                ))}
              </Select>
              {(error || hint) && (
                <FormHelperText>{error?.message?.toString() || hint}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      );

    case 'FILE':
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={null}
          render={({ field: { onChange, ref } }) => (
            <FormControl
              fullWidth
              error={!!error}
              margin="normal"
              required={field.required}
            >
              <FormLabel>{label}</FormLabel>
              <Box sx={{ mt: 1 }}>
                <input
                  type="file"
                  onChange={(e) => onChange(e.target.files?.[0] || null)}
                  ref={ref}
                  disabled={field.readOnly}
                />
              </Box>
              {(error || hint) && (
                <FormHelperText>{error?.message?.toString() || hint}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      );

    default:
      return (
        <Controller
          name={field.processVariable}
          control={control}
          defaultValue={field.defaultValue || ''}
          render={({ field: { onChange, value, ref } }) => (
            <TextField
              fullWidth
              label={label}
              value={value || ''}
              onChange={onChange}
              inputRef={ref}
              margin="normal"
            />
          )}
        />
      );
  }
}

function evaluateCondition(
  watchedValue: unknown,
  conditional: NonNullable<FormField['conditional']>
): boolean {
  const { operator, value } = conditional;
  const strWatchedValue = String(watchedValue || '');

  switch (operator) {
    case 'EQUALS':
      return strWatchedValue === value;
    case 'NOT_EQUALS':
      return strWatchedValue !== value;
    case 'CONTAINS':
      return strWatchedValue.includes(value);
    case 'GREATER_THAN':
      return Number(watchedValue) > Number(value);
    case 'LESS_THAN':
      return Number(watchedValue) < Number(value);
    default:
      return true;
  }
}
